import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { getRecommendations } from '../lib/recommendations';
import { getWeatherRecommendation } from '../lib/weather';
import { Tobacco, Event } from '../lib/supabase';
import BottomNav from '../components/BottomNav';
import DrinkPairingBox from '../components/DrinkPairingBox';
import TobaccoCard from '../components/TobaccoCard';
import EventCard from '../components/EventCard';
import EventPopup from '../components/EventPopup';
import LoadingSpinner from '../components/LoadingSpinner';
import { SkeletonCard, SkeletonEventCard } from '../components/SkeletonLoader';
import Button from '../components/ui/Button';
import { Cloud, Thermometer, Calendar, ChevronDown, ChevronUp, MapPin, Clock, User, Users, Search, UserPlus, Check, X } from 'lucide-react';

export default function Discover() {
  const { currentUser } = useEmberStore();
  const [recommendations, setRecommendations] = useState<Tobacco[]>([]);
  const [weather, setWeather] = useState<any>(null);
  const [userCoords, setUserCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'pipe' | 'cigar'>('all');
  const STRENGTH_OPTIONS: Array<'mild' | 'mild-medium' | 'medium' | 'medium-strong' | 'strong'> = ['mild', 'mild-medium', 'medium', 'medium-strong', 'strong'];
  const [strengthFilter, setStrengthFilter] = useState<'all' | 'mild' | 'mild-medium' | 'medium' | 'medium-strong' | 'strong'>('all');
  const CUTS = ['Flake','Ribbon','Plug','Ready Rubbed','Cube','Coin','Shag','Crumble Cake','Rope'] as const;
  const [cutFilter, setCutFilter] = useState<'all' | typeof CUTS[number]>('all');
  
  // User search state
  const [userSearch, setUserSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState<any[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [processingConnectionIds, setProcessingConnectionIds] = useState<Set<string>>(new Set());
  const [membersExpanded, setMembersExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    loadUpcomingEvents();
    loadRecentPublicProfiles();

    const fetchRecommendations = (lat?: number, lon?: number) => {
      loadRecommendations(lat, lon);
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;
          const coords = {
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          };
          setUserCoords(coords);
          fetchRecommendations(coords.lat, coords.lon);
        },
        (error) => {
          console.warn('Discover weather geolocation failed:', error);
          if (!cancelled) {
            fetchRecommendations();
          }
        },
        { enableHighAccuracy: true, timeout: 5000 }
      );
    } else {
      fetchRecommendations();
    }

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // Debounced user search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (userSearch.trim().length >= 1) {
        searchUsers(userSearch.trim());
      } else {
        // Reload recent profiles when search is cleared
        loadRecentPublicProfiles();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [userSearch]);

  async function loadRecommendations(lat?: number, lon?: number) {
    try {
      setLoading(true);
      const weatherData = await getWeatherRecommendation(lat, lon);
      setWeather(weatherData);

      const q = search.trim().toLowerCase();

      // Helper to match UI strength buckets to DB values
      const matchesStrength = (dbStrength?: string) => {
        if (strengthFilter === 'all') return true;
        if (!dbStrength) return false;
        const normalized = dbStrength.toLowerCase();
        // Map legacy values (full, medium-full, etc.)
        const legacyMap: Record<string, 'mild' | 'mild-medium' | 'medium' | 'medium-strong' | 'strong'> = {
          'full': 'strong',
          'medium-full': 'medium-strong',
          'medium full': 'medium-strong',
          'light': 'mild',
          'medium/mild': 'mild-medium',
        };
        const canonical = ((): 'mild' | 'mild-medium' | 'medium' | 'medium-strong' | 'strong' | null => {
          if (STRENGTH_OPTIONS.includes(normalized as any)) return normalized as any;
          if (legacyMap[normalized]) return legacyMap[normalized];
          return null;
        })();
        if (!canonical) return false;
        return canonical === strengthFilter;
      };

      const hasActiveFilters = (
        q.length > 0 ||
        typeFilter !== 'all' ||
        cutFilter !== 'all' ||
        strengthFilter !== 'all'
      );

      if (hasActiveFilters) {
        // SEARCH MODE: fetch full table, then filter client-side across name, brand, flavor_tags, cut
        const { data: tobaccos, error } = await supabase
          .from('tobaccos')
          .select('*');
        if (error || !tobaccos) throw error;

        const results = tobaccos
          .filter((t) => (typeFilter === 'all' ? true : t.type === typeFilter))
          .filter((t) => (cutFilter === 'all' ? true : String((t as any).cut || '') === cutFilter))
          .filter((t) => matchesStrength(t.strength))
          .filter((t) => {
            const name = (t.name || '').toLowerCase();
            const brand = (t.brand || '').toLowerCase();
            const cut = (t as any).cut ? String((t as any).cut).toLowerCase() : '';
            const tags: string[] = Array.isArray((t as any).flavor_tags) ? (t as any).flavor_tags : [];
            const tagHit = tags.some((x) => String(x).toLowerCase().includes(q));
            return (
              name.includes(q) || brand.includes(q) || cut.includes(q) || tagHit
            );
          });

        setRecommendations(results);
      } else {
        // RECOMMENDATION MODE: use weather-based top list, then apply filters
        const recs = await getRecommendations(
          lat,
          lon,
          currentUser?.smoker_type,
          currentUser?.pipe_shapes,
          weatherData
        );

        let filtered = recs;
        if (typeFilter !== 'all') filtered = filtered.filter((t) => t.type === typeFilter);
        if (cutFilter !== 'all') filtered = filtered.filter((t) => String((t as any).cut || '') === cutFilter);
        filtered = filtered.filter((t) => matchesStrength(t.strength));
        setRecommendations(filtered);
      }
    } catch (err) {
      console.error('Error loading recommendations:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadUpcomingEvents() {
    try {
      const now = new Date();
      // Add 5-minute buffer to account for timing differences and timezone conversions
      const bufferTime = new Date(now.getTime() - 5 * 60 * 1000);

      const { data, error } = await supabase
        .from('events')
        .select(`
          *,
          venue:venues(*),
          creator:profiles!events_creator_id_fkey(username),
          responses:event_responses(*)
        `)
        .gte('start_datetime', bufferTime.toISOString())
        .order('start_datetime', { ascending: true })
        .limit(10);

      if (error) throw error;
      if (data) setUpcomingEvents(data as any);
    } catch (err) {
      console.error('Error loading upcoming events:', err);
    }
  }

  async function loadRecentPublicProfiles() {
    try {
      const { data, error } = await supabase.rpc('get_recent_public_profiles', {
        limit_count: 8
      });

      if (error) throw error;
      if (data) setUserSearchResults(data);
    } catch (err) {
      console.error('Error loading recent public profiles:', err);
    }
  }

  async function searchUsers(searchTerm: string) {
    if (!searchTerm.trim()) {
      setUserSearchResults([]);
      return;
    }

    setUserSearchLoading(true);
    try {
      const { data, error } = await supabase.rpc('search_users', {
        search_term: searchTerm,
        limit_count: 20
      });

      if (error) throw error;
      if (data) setUserSearchResults(data);
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setUserSearchLoading(false);
    }
  }

  async function handleSendConnectionRequest(targetUserId: string) {
    setProcessingConnectionIds(new Set([...processingConnectionIds, targetUserId]));
    try {
      const { error } = await supabase.rpc('send_connection_request', {
        target_user: targetUserId
      });

      if (error) throw error;
      
      // Update local state to show "Request Sent"
      setUserSearchResults(prev => 
        prev.map(user => 
          user.id === targetUserId 
            ? { ...user, connection_status: 'pending', can_send_request: false }
            : user
        )
      );
    } catch (err) {
      console.error('Error sending connection request:', err);
    } finally {
      setProcessingConnectionIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(targetUserId);
        return newSet;
      });
    }
  }

  async function handleAcceptConnection(connectionId: string) {
    setProcessingConnectionIds(new Set([...processingConnectionIds, connectionId]));
    try {
      const { error } = await supabase.rpc('accept_connection_request', {
        connection_id: connectionId
      });

      if (error) throw error;
      
      // Update local state
      setUserSearchResults(prev => 
        prev.map(user => 
          user.connection_id === connectionId 
            ? { ...user, connection_status: 'accepted' }
            : user
        )
      );
    } catch (err) {
      console.error('Error accepting connection:', err);
    } finally {
      setProcessingConnectionIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(connectionId);
        return newSet;
      });
    }
  }

  async function handleRemoveConnection(connectionId: string) {
    setProcessingConnectionIds(new Set([...processingConnectionIds, connectionId]));
    try {
      const { error } = await supabase.rpc('remove_connection', {
        connection_id: connectionId
      });

      if (error) throw error;
      
      // Update local state
      setUserSearchResults(prev => 
        prev.map(user => 
          user.connection_id === connectionId 
            ? { ...user, connection_status: 'none', can_send_request: true }
            : user
        )
      );
    } catch (err) {
      console.error('Error removing connection:', err);
    } finally {
      setProcessingConnectionIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(connectionId);
        return newSet;
      });
    }
  }


  return (
    <div style={{ minHeight: '100vh', paddingBottom: '120px' }}>
      <header style={{
        padding: '1rem',
        borderBottom: '1px solid var(--color-border)',
        position: 'sticky',
        top: 0,
        backgroundColor: '#0b0b0b',
        zIndex: 10,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <h1 style={{ fontSize: '1.5rem' }}>Discover</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Link to="/map">
            <button style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--color-secondary)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              <MapPin size={16} />
              Map
            </button>
          </Link>
          <Link to="/create-event">
            <button style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--color-secondary)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}>
              <Calendar size={16} />
              Create Event
            </button>
          </Link>
        </div>
      </header>

      <div className="container" style={{ paddingTop: '1rem' }}>
        {/* Pairing Box - online only, additive and safe */}
        <DrinkPairingBox />
        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Calendar size={20} color="var(--color-accent)" />
              <h3 style={{ fontSize: '1.125rem', color: 'var(--color-accent)' }}>Upcoming Events</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {upcomingEvents.map((event) => {
                const userResponse = event.responses?.find(r => r.user_id === currentUser?.id);
                const isNotInterested = userResponse?.response_type === 'not_interested';
                
                return (
                  <div 
                    key={event.id}
                    style={{ 
                      opacity: isNotInterested ? 0.6 : 1,
                      filter: isNotInterested ? 'grayscale(0.5)' : 'none'
                    }}
                  >
                    <EventCard 
                      event={event}
                      currentUserId={currentUser?.id}
                      onClick={() => setSelectedEvent(event)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* User Search Section */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Users size={20} color="var(--color-accent)" />
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>Find Pipe & Cigar Enthusiasts</h3>
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
              <input
                type="text"
                placeholder="Search by username..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                style={{ 
                  width: '100%', 
                  paddingLeft: '40px',
                  paddingRight: userSearchLoading ? '40px' : '12px'
                }}
              />
              {userSearchLoading && (
                <div style={{ 
                  position: 'absolute', 
                  right: '12px', 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  border: '2px solid #999',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  width: '16px',
                  height: '16px',
                  animation: 'spin 0.8s linear infinite'
                }} />
              )}
            </div>
          </div>

          {/* User Search Results */}
          {userSearchResults.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <h4 style={{ fontSize: '0.875rem', color: '#999', fontWeight: '600' }}>
                  {userSearch.trim() ? `Search Results (${userSearchResults.length})` : 'Recent Members'}
                </h4>
                {!userSearch.trim() && (
                  <button
                    onClick={() => setMembersExpanded(!membersExpanded)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#999',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '0.25rem',
                    }}
                    aria-label={membersExpanded ? 'Hide members' : 'Show members'}
                  >
                    {membersExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {(userSearch.trim() ? userSearchResults : (membersExpanded ? userSearchResults : userSearchResults.slice(0, 3))).map((user) => (
                  <div
                    key={user.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      padding: '0.75rem',
                      backgroundColor: 'rgba(11, 11, 11, 0.3)',
                      borderRadius: '8px',
                      border: '1px solid var(--color-border)'
                    }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      backgroundColor: '#5a3310',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {user.avatar_url ? (
                        <img src={user.avatar_url} alt={user.username} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <UserPlus size={24} color="#ff9500" />
                      )}
                    </div>

                    {/* User Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>
                        {user.username}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'capitalize' }}>
                        {user.smoker_type === 'both' ? 'Pipe & Cigar' : user.smoker_type} Enthusiast
                      </div>
                      {user.pipe_shapes && user.pipe_shapes.length > 0 && (
                        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                          {user.pipe_shapes.slice(0, 3).join(', ')}
                          {user.pipe_shapes.length > 3 && ' +more'}
                        </div>
                      )}
                    </div>

                    {/* Connection Action */}
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {user.connection_status === 'none' && user.can_send_request && (
                        <button
                          onClick={() => handleSendConnectionRequest(user.id)}
                          disabled={processingConnectionIds.has(user.id)}
                          style={{
                            padding: '0.5rem 0.75rem',
                            backgroundColor: 'var(--color-accent)',
                            border: 'none',
                            color: '#0b0b0b',
                            borderRadius: '6px',
                            cursor: processingConnectionIds.has(user.id) ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            opacity: processingConnectionIds.has(user.id) ? 0.6 : 1
                          }}
                        >
                          <UserPlus size={14} />
                          Connect
                        </button>
                      )}
                      
                      {user.connection_status === 'pending' && (
                        <div style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: 'rgba(255, 165, 0, 0.1)',
                          border: '1px solid rgba(255, 165, 0, 0.3)',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          color: '#ffa500',
                          fontWeight: '500'
                        }}>
                          Request Sent
                        </div>
                      )}
                      
                      {user.connection_status === 'accepted' && (
                        <div style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: 'rgba(107, 175, 74, 0.1)',
                          border: '1px solid rgba(107, 175, 74, 0.3)',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          color: 'var(--color-success)',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.35rem'
                        }}>
                          <Check size={14} />
                          Connected
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {userSearch.trim() && userSearchResults.length === 0 && !userSearchLoading && (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
              <Users size={32} style={{ margin: '0 auto 0.5rem', opacity: 0.5 }} />
              <p>No users found matching "{userSearch}"</p>
              <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>Try a different search term</p>
            </div>
          )}
        </div>

        {/* Weather Widget */}
        {weather && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <Cloud size={24} color="#d3de5e" />
              <h3 style={{ fontSize: '1.125rem', color: '#d3de5e' }}>Weather Conditions</h3>
            </div>
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
              {weather.temperature !== null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Thermometer size={16} />
                  <span>{weather.temperature}°C</span>
                </div>
              )}
              <div>Season: {weather.season}</div>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#d3de5e' }}>
              {weather.recommendation}
            </p>
          </div>
        )}

        {/* Tobacco Filters */}
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Search size={20} color="var(--color-accent)" />
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>Tobacco Search</h3>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search by name or brand"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: '220px' }}
            />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as any)}>
              <option value="all">All</option>
              <option value="pipe">Pipe</option>
              <option value="cigar">Cigar</option>
            </select>
            <select value={strengthFilter} onChange={(e) => setStrengthFilter(e.target.value as any)}>
              <option value="all">Any strength</option>
              {STRENGTH_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt.replace('-', ' ')}</option>
              ))}
            </select>
            <select value={cutFilter} onChange={(e) => setCutFilter(e.target.value as any)}>
              <option value="all">Any cut</option>
              {CUTS.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <Button variant="secondary" onClick={() => loadRecommendations(userCoords?.lat, userCoords?.lon)}>Apply</Button>
          </div>
        </div>

        {/* Recommendations */}
        <h2 style={{ fontSize: '1.25rem', marginBottom: '1rem' }}>Recommended for You</h2>

        {loading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : recommendations.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: '#d3de5e' }}>
              No recommendations available. Check back later!
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {recommendations.map((tobacco) => (
              <TobaccoCard key={tobacco.id} tobacco={tobacco} />
            ))}
          </div>
        )}
      </div>

      <BottomNav />

      {/* Event Popup */}
      {selectedEvent && (
        <EventPopup
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onResponseUpdate={() => {
            loadUpcomingEvents();
            setSelectedEvent(null);
          }}
        />
      )}
    </div>
  );
}

